import { SectionHead, FadeUp } from "./Reveal";
import { openQuote } from "../lib/lenis";

const EFFECTS = [
  ["Opens the thoracic cage", "Releases the chest wall and lifts the sternum."],
  ["Releases the upper body", "Tension through upper back, shoulders, arms and wrists lets go."],
  ["Restores breath & posture", "Breathing deepens, posture lifts, movement frees up."],
  ["Shifts the nervous system", "From survival into regulation — often with an emotional release."],
];

export const Technique = () => (
  <section id="technique" className="relative py-24 lg:py-32 bg-ink2/50 border-y border-white/10 overflow-hidden" data-testid="technique-section">
    <div className="spotlight pointer-events-none absolute top-1/3 left-[-10%] h-[36rem] w-[36rem] rounded-full" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-6">
        <SectionHead
          eyebrow="Signature method"
          title={<>The pain isn't always where <em className="italic text-gold">the problem is</em>.</>}
          sub="Fascia — the connective tissue wrapping every muscle, bone, nerve and organ — is the body's internal web. Stress, injury, repetition and emotional tension lock restrictions into it: pain, stiffness, poor posture, limited mobility."
        />
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {EFFECTS.map(([t, d], i) => (
            <FadeUp key={t} delay={i * 0.06}>
              <div className="h-full rounded-xl border border-white/10 bg-ink p-5" data-testid={`technique-effect-${i}`}>
                <p className="font-mono text-[10px] text-gold tracking-[0.2em]">0{i + 1}</p>
                <h3 className="mt-3 font-serif text-xl text-ivory font-light">{t}</h3>
                <p className="mt-2 text-sm text-mute leading-relaxed">{d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.1}>
          <p className="mt-10 max-w-xl font-serif italic text-xl sm:text-2xl text-ivory/90 leading-snug" data-testid="technique-credo">
            “Everything I do connects back to fascia — and fascia connects everything in the body.”
          </p>
          <button
            data-testid="technique-cta"
            onClick={() => openQuote("corporate")}
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-gold/50 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            Bring the method to your team →
          </button>
        </FadeUp>
      </div>
      <div className="lg:col-span-6">
        <FadeUp>
          <div className="relative">
            <div className="clip-notch overflow-hidden border border-white/10" data-testid="technique-image">
              <img src="https://images.unsplash.com/photo-1741522509438-a120c0bb5e88?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Chest wall decompression — hands-on fascia release" loading="lazy"
                className="h-[26rem] lg:h-[34rem] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" aria-hidden="true" />
            </div>
            <div className="glass-chip absolute bottom-6 left-6 right-6 rounded-xl p-5" data-testid="technique-signature">
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Signature technique</p>
              <h3 className="mt-2 font-serif text-2xl text-ivory font-light">Chest Wall Decompression</h3>
              <p className="mt-2 text-sm text-mute leading-relaxed">
                The desk-day antidote: undoes hours of sitting and screen posture in one focused session.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);
